import React from "react";
import { Link } from "react-router-dom";
import { fmtDate } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UserAvatar } from "@/components/UserAvatar";
import { Camera, Loader2, Trash2 } from "lucide-react";

const STATUS_LABEL = { trial: "Free trial", founding: "Founding member", active: "Active licence", past_due: "Payment overdue", cancelled: "Cancelled" };

export const ReadOnly = ({ label, value, testId, children }) => (
  <div data-testid={testId}>
    <Label className="text-muted-foreground">{label}</Label>
    <div className="h-10 flex items-center px-3 rounded-md border border-cream-deep bg-cream text-sm text-brand/80 truncate">{value || "—"}{children}</div>
  </div>
);

function statusText(h) {
  if (!h) return "";
  const base = STATUS_LABEL[h.subscription_status] || h.subscription_status || "";
  if (h.subscription_status === "trial" || h.subscription_status === "founding") return h.trial_expired ? "Trial ended" : `${base} · ends ${fmtDate(h.trial_ends_at)}`;
  if (h.renewal_date) return `${base} · renews ${fmtDate(h.renewal_date)}`;
  return base;
}

export function ProfilePhotoPicker({ p, role, uploading, fileRef, onUpload, onRemove }) {
  return (
    <div className="flex flex-wrap items-center gap-5">
      <UserAvatar testId="my-profile-photo" name={p.name} url={p.photo_url} className="h-24 w-24 text-3xl bg-brand text-white" />
      <div className="space-y-2">
        <div className="font-heading font-bold text-brand">Profile photo</div>
        <p className="text-xs text-muted-foreground max-w-xs">PNG, JPG or WEBP up to 8MB. Shown in your sidebar{role === "manager" ? " and on your care home's manager card" : ""}.</p>
        <div className="flex gap-2">
          <input ref={fileRef} data-testid="my-profile-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files[0])} />
          <Button type="button" size="sm" variant="outline" data-testid="my-profile-photo-btn" disabled={uploading} onClick={() => fileRef.current?.click()} className="rounded-full h-8">
            {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Camera className="h-3.5 w-3.5 mr-1" />}{p.photo_url ? "Change photo" : "Add photo"}
          </Button>
          {p.photo_url && <Button type="button" size="sm" variant="ghost" data-testid="my-profile-photo-remove" onClick={onRemove} className="rounded-full h-8 text-rag-red"><Trash2 className="h-3.5 w-3.5 mr-1" />Remove</Button>}
        </div>
      </div>
    </div>
  );
}

export function CareHomeFields({ role, home, form, set }) {
  const isManager = role === "manager";
  return (
    <>
      <div><Label>{isManager ? "Job title" : "Job title / position"}</Label><Input data-testid="my-profile-job-role" value={form.job_role} onChange={set("job_role")} placeholder={isManager ? "e.g. Registered Manager" : "e.g. Senior Care Assistant"} /></div>
      {home && (
        <ReadOnly label={isManager ? "Care home you manage" : "Your care home"} value={home.name} testId="my-profile-care-home">
          {isManager && <a href="#care-home-settings" data-testid="my-profile-care-home-link" className="ml-auto text-xs text-coral font-semibold hover:underline">Settings</a>}
        </ReadOnly>
      )}
      {isManager && home && (
        <ReadOnly label="Licence status" value={statusText(home)} testId="my-profile-status">
          {home.subscription_status !== "active" && <Link to="/app/billing" className="ml-auto text-xs text-coral font-semibold hover:underline">Billing</Link>}
        </ReadOnly>
      )}
    </>
  );
}

export function TrainerFields({ p, form, set }) {
  return (
    <>
      <ReadOnly label="Train the Trainer certificate" testId="my-profile-tt-cert"
        value={p.tt_certificate ? `${p.tt_certificate.cert_number} · ${fmtDate(p.tt_certificate.issued_at)}` : "Not on record"} />
      <div className="md:col-span-2"><Label>Short bio <span className="text-muted-foreground font-normal">(shown to care homes alongside your sessions)</span></Label>
        <Textarea data-testid="my-profile-bio" rows={4} maxLength={1000} value={form.trainer_bio} onChange={set("trainer_bio")} /></div>
    </>
  );
}
